import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { IconButton } from "@a2z/ui";
import { ModalSheet } from "@/shared/components/dialogs/ModalSheet";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { useIsMobile } from "@/shared/hooks/useIsMobile";
import type { ProductActionTarget } from "@/modules/products/lib/useProductCardActions";

type Props = {
  target: ProductActionTarget;
  editHref: string;
  /** Absolute storefront URL — opened in a new tab. */
  previewHref: string;
  pendingVisibility: boolean;
  onToggleVisibility: () => void;
  onRequestDelete: () => void;
  /** Open the inline stock editor. Omit to hide the item. */
  onEditStock?: () => void;
  /** Extra classes for the trigger button (e.g. sizing). */
  triggerClassName?: string;
};

/**
 * Per-product overflow menu (Edit · Show/Hide · Open in store · Delete).
 * Desktop → anchored dropdown; mobile → bottom action sheet. Shared by the
 * grid card, table row and mobile row so the action set stays identical.
 */
export function ProductActionsMenu({
  target,
  editHref,
  previewHref,
  pendingVisibility,
  onToggleVisibility,
  onRequestDelete,
  onEditStock,
  triggerClassName,
}: Props) {
  const t = useTranslations();
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  // Desktop: anchor a fixed-position dropdown to the trigger. The cards/rows
  // use `overflow-hidden`, so a normally-positioned popover would be clipped —
  // a portal to <body> escapes the clip.
  const MENU_WIDTH = 200;
  useLayoutEffect(() => {
    if (!open || isMobile) return;
    const place = () => {
      const r = triggerRef.current?.getBoundingClientRect();
      if (!r) return;
      const left = Math.min(
        Math.max(r.right - MENU_WIDTH, 8),
        window.innerWidth - MENU_WIDTH - 8,
      );
      const estHeight = menuRef.current?.offsetHeight ?? 220;
      const top =
        r.bottom + 4 + estHeight > window.innerHeight
          ? Math.max(r.top - estHeight - 4, 8)
          : r.bottom + 4;
      setCoords({ top, left });
    };
    place();
  }, [open, isMobile]);

  // Desktop: close on outside click / Escape / scroll. (Mobile uses
  // ModalSheet's own dismiss handling.)
  useEffect(() => {
    if (!open || isMobile) return;
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        !containerRef.current?.contains(target) &&
        !menuRef.current?.contains(target)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onScroll = () => setOpen(false);
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open, isMobile]);

  const close = () => setOpen(false);

  const items: ReactNode = (
    <>
      <MenuLink to={editHref} onSelect={close} icon={<EditIcon />}>
        {t(keys.common.edit)}
      </MenuLink>
      {onEditStock ? (
        <MenuButton
          onSelect={() => {
            close();
            onEditStock();
          }}
          icon={<BoxIcon />}
        >
          {t(keys.products.variantsEditStock)}
        </MenuButton>
      ) : null}
      <MenuButton
        onSelect={() => {
          close();
          onToggleVisibility();
        }}
        disabled={pendingVisibility}
        icon={target.isActive ? <EyeOffIcon /> : <EyeIcon />}
      >
        {target.isActive ? t(keys.products.actionHide) : t(keys.products.actionShow)}
      </MenuButton>
      <MenuAnchor href={previewHref} onSelect={close} icon={<ExternalIcon />}>
        {t(keys.products.actionPreview)}
      </MenuAnchor>
      <div className="my-1 h-px bg-[color:var(--border-light)]" aria-hidden="true" />
      <MenuButton
        onSelect={() => {
          close();
          onRequestDelete();
        }}
        danger
        icon={<TrashIcon />}
      >
        {t(keys.common.delete)}
      </MenuButton>
    </>
  );

  const trigger = (
    <IconButton
      ref={triggerRef}
      type="button"
      ariaLabel={t(keys.products.moreActions)}
      aria-haspopup="menu"
      aria-expanded={open}
      onClick={(e) => {
        // Rows/cards are wrapped in links — don't let the click bubble to them.
        e.preventDefault();
        e.stopPropagation();
        setOpen((v) => !v);
      }}
      variant="ghost"
      size="sm"
      shape="square"
      className={triggerClassName}
      icon={<DotsIcon />}
    />
  );

  if (isMobile) {
    return (
      <div ref={containerRef} className="inline-flex">
        {trigger}
        <ModalSheet isOpen={open} onClose={close}>
          <div role="menu" className="flex flex-col px-2 pb-2 pt-1">
            {items}
          </div>
        </ModalSheet>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative inline-flex">
      {trigger}
      {open && typeof document !== "undefined"
        ? createPortal(
            <div
              ref={menuRef}
              role="menu"
              style={{
                position: "fixed",
                top: coords?.top ?? -9999,
                left: coords?.left ?? -9999,
                width: MENU_WIDTH,
              }}
              className="z-[60] flex flex-col rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface)] p-1.5 shadow-[var(--shadow-lg)]"
            >
              {items}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

// ── Menu row primitives ──────────────────────────────────────────────────────

const ROW_BASE =
  "flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2.5 text-start text-[14px] font-medium no-underline transition-colors disabled:opacity-50 md:py-2";
const ROW_NEUTRAL =
  "text-[color:var(--text-primary)] hover:bg-[color:var(--background-muted)]";
const ROW_DANGER =
  "text-[color:var(--error-500)] hover:bg-[color:var(--error-50)]";

function MenuLink({
  to,
  onSelect,
  icon,
  children,
}: {
  to: string;
  onSelect: () => void;
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <Link role="menuitem" to={to} onClick={onSelect} className={`${ROW_BASE} ${ROW_NEUTRAL}`}>
      <span className="text-[color:var(--text-muted)]">{icon}</span>
      {children}
    </Link>
  );
}

function MenuAnchor({
  href,
  onSelect,
  icon,
  children,
}: {
  href: string;
  onSelect: () => void;
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <a
      role="menuitem"
      href={href}
      target="_blank"
      rel="noreferrer"
      onClick={onSelect}
      className={`${ROW_BASE} ${ROW_NEUTRAL}`}
    >
      <span className="text-[color:var(--text-muted)]">{icon}</span>
      {children}
    </a>
  );
}

function MenuButton({
  onSelect,
  icon,
  children,
  danger,
  disabled,
}: {
  onSelect: () => void;
  icon: ReactNode;
  children: ReactNode;
  danger?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onSelect();
      }}
      className={`${ROW_BASE} ${danger ? ROW_DANGER : ROW_NEUTRAL}`}
    >
      <span className={danger ? "" : "text-[color:var(--text-muted)]"}>{icon}</span>
      {children}
    </button>
  );
}

// ── Icons ────────────────────────────────────────────────────────────────────

const svgProps = {
  width: 16,
  height: 16,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};

function DotsIcon() {
  return (
    <svg {...svgProps}>
      <circle cx="12" cy="5" r="1" />
      <circle cx="12" cy="12" r="1" />
      <circle cx="12" cy="19" r="1" />
    </svg>
  );
}

function EditIcon() {
  return (
    <svg {...svgProps}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}

function BoxIcon() {
  return (
    <svg {...svgProps}>
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      <path d="M3.27 6.96 12 12.01l8.73-5.05" />
      <path d="M12 22.08V12" />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg {...svgProps}>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg {...svgProps}>
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <path d="M1 1l22 22" />
    </svg>
  );
}

function ExternalIcon() {
  return (
    <svg {...svgProps}>
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <path d="M15 3h6v6" />
      <path d="M10 14 21 3" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg {...svgProps}>
      <path d="M3 6h18" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  );
}
